import { useState, useEffect, useMemo } from "react";
import Modal from "components/pages/Home/components/Filter/Modal";
import Typography from "components/elements/Typography";
import Button, { EButtonColor, EButtonSize, EButtonVariant } from "components/elements/Button";
import Select from "components/materials/Form/SelectElement";
import I18n from "components/materials/I18n";
import { EProjectFormat } from "common/enums/ProjectFormat/ProjectFormat";
import { EProjectTheme } from "common/enums/ProjectTheme/ProjectTheme";
import { EProjectSortOption } from "../../Tag";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ReactComponent as DropdownIcon } from "assets/images/Icons/dropdown.svg";
import classes from "./classes.module.scss";

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	selectedFormat: EProjectFormat | null;
	selectedSort: EProjectSortOption | null;
	selectedThemes: EProjectTheme[];
	onApply: (format: EProjectFormat | null, sort: EProjectSortOption | null, themes: EProjectTheme[]) => void;
	allProjects: ProjectResponseResource[];
	searchQuery: string;
};

export default function FilterModal(props: IProps) {
	const [format, setFormat] = useState<EProjectFormat | null>(props.selectedFormat);
	const [sort, setSort] = useState<EProjectSortOption | null>(props.selectedSort);
	const [themes, setThemes] = useState<EProjectTheme[]>(props.selectedThemes);

	useEffect(() => {
		setFormat(props.selectedFormat);
		setSort(props.selectedSort);
		setThemes(props.selectedThemes);
	}, [props.selectedFormat, props.selectedSort, props.selectedThemes, props.isOpen]);

	const formatOptions = [
		{ label: I18n.trslt(I18n.asset.pages.projects.filter.format_placeholder), value: "" },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.format[EProjectFormat.long_metrage]), value: EProjectFormat.long_metrage },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.format[EProjectFormat.court_metrage]), value: EProjectFormat.court_metrage },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.format[EProjectFormat.serie]), value: EProjectFormat.serie },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.format[EProjectFormat.documentaire]), value: EProjectFormat.documentaire },
	];

	const sortOptions = [
		{ label: I18n.trslt(I18n.asset.pages.projects.filter.sort_placeholder), value: "" },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.sort[EProjectSortOption.NEWEST]), value: EProjectSortOption.NEWEST },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.sort[EProjectSortOption.OLDEST]), value: EProjectSortOption.OLDEST },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.sort[EProjectSortOption.MOST_LIKED]), value: EProjectSortOption.MOST_LIKED },
		{ label: I18n.trslt(I18n.asset.pages.projects.tag.sort[EProjectSortOption.LEAST_LIKED]), value: EProjectSortOption.LEAST_LIKED },
	];

	const allThemes = Object.values(EProjectTheme);

	const handleClearAll = () => {
		setFormat(null);
		setSort(null);
		setThemes([]);
	};

	const handleApply = () => {
		props.onApply(format, sort, themes);
		props.onClose();
	};

	const toggleTheme = (theme: EProjectTheme) => {
		setThemes((prev) => (prev.includes(theme) ? prev.filter((t) => t !== theme) : [...prev, theme]));
	};

	const previewCount = useMemo(() => {
		let filtered = [...props.allProjects];

		// Apply search filter (already applied in parent, so we mirror it)
		if (props.searchQuery.trim()) {
			const query = props.searchQuery.toLowerCase();
			filtered = filtered.filter((project) => {
				const title = project.title?.toLowerCase() || "";
				const pitch = project.pitch?.toLowerCase() || "";
				const authorName = `${project.author?.firstName || ""} ${project.author?.lastName || ""}`.toLowerCase();

				return title.includes(query) || pitch.includes(query) || authorName.includes(query);
			});
		}

		// Apply format filter (temporary state)
		if (format) {
			filtered = filtered.filter((project) => project.format === format);
		}

		// Apply theme filter (temporary state)
		if (themes.length > 0) {
			filtered = filtered.filter((project) => themes.includes(project.theme));
		}

		return filtered.length;
	}, [props.allProjects, props.searchQuery, format, themes]);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="h2" weight="bold" className={classes["title"]}>
					Filtres de recherche
				</Typography>
				<button type="button" className={classes["close-button"]} onClick={props.onClose}>
					<XMarkIcon />
				</button>
			</div>

			<div className={classes["content"]}>
				<div className={classes["section"]}>
					<Typography type="p" weight="medium" className={classes["section-title"]}>
						{I18n.trslt(I18n.asset.pages.projects.filter.format_label)}
					</Typography>
					<Select
						name="format"
						icon={<DropdownIcon />}
						options={formatOptions}
						defaultValue={format || ""}
						onChange={(e) => setFormat(e.target.value ? (e.target.value as EProjectFormat) : null)}
						className={[classes["select"], !format ? classes["select-placeholder"] : ""].join(" ")}
					/>
				</div>

				<div className={classes["section"]}>
					<Typography type="p" weight="medium" className={classes["section-title"]}>
						{I18n.trslt(I18n.asset.pages.projects.filter.sort_label)}
					</Typography>
					<Select
						name="sort"
						icon={<DropdownIcon />}
						options={sortOptions}
						defaultValue={sort || ""}
						onChange={(e) => setSort(e.target.value ? (e.target.value as EProjectSortOption) : null)}
						className={[classes["select"], !sort ? classes["select-placeholder"] : ""].join(" ")}
					/>
				</div>

				<div className={classes["theme-section"]}>
					<div className={classes["theme-section-header"]}>
						<Typography type="p" weight="medium" className={classes["section-title"]}>
							{I18n.trslt(I18n.asset.pages.projects.filter.theme_label)}
						</Typography>
						<Typography type="span" weight="regular" size="medium">
							{I18n.trslt(I18n.asset.pages.projects.filter.theme_placeholder)}
						</Typography>
					</div>
					<div className={classes["theme-chips"]}>
						{allThemes.map((theme) => (
							<button
								key={theme}
								type="button"
								className={[classes["theme-chip"], themes.includes(theme) ? classes["selected"] : ""].join(" ")}
								onClick={() => toggleTheme(theme)}>
								<Typography type="span" fontFamily="secondary" typoFont="primary" weight="regular" size="small">
									{I18n.trslt(I18n.asset.pages.projects.tag.theme[theme])}
								</Typography>
							</button>
						))}
					</div>
				</div>
			</div>

			<div className={classes["footer"]}>
				<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} size={EButtonSize.LARGE} onClick={handleClearAll}>
					<Typography type="span" weight="medium" size="medium">
						{I18n.trslt(I18n.asset.pages.projects.filter.clear_all)}
					</Typography>
				</Button>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.MEDIUM} onClick={handleApply}>
					<Typography type="span" weight="medium" size="medium">
						{I18n.trslt(I18n.asset.pages.projects.filter.show_projects).replace("{count}", previewCount.toString())}
					</Typography>
				</Button>
			</div>
		</Modal>
	);
}
